import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { validateQuoteToken } from '@/lib/supabase/tokens';
import { getQuoteById, bookQuoteVisit, submitQuotePrice, declineQuote } from '@/lib/supabase/quotes';
import { Quote } from '@/lib/types';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';
import { CheckCircle, XCircle, Calendar as CalendarIcon, Send, AlertCircle, Clock, Info, Shield } from 'lucide-react';
import { debug } from '@/lib/utils';

/**
 * Error types for the trade response page
 */
enum ErrorType {
  TOKEN_EXPIRED = 'token_expired',
  TOKEN_INVALID = 'token_invalid',
  QUOTE_NOT_FOUND = 'quote_not_found',
  SERVER_ERROR = 'server_error',
  UNKNOWN = 'unknown'
}

/**
 * Public page for tradespeople to respond to quote requests
 * Accessed via magic link token in the URL
 */
export default function TradeQuoteResponsePage() {
  const { token } = useParams<{ token: string }>();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDates, setSelectedDates] = useState<Array<{date: Date, time: string}>>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);  // Added for single date selection
  const [selectedTime, setSelectedTime] = useState<string | null>(null); // Added for single time selection
  const [priceText, setPriceText] = useState('');
  const [customReply, setCustomReply] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<ErrorType | null>(null);
  const [graceTimeRemaining, setGraceTimeRemaining] = useState<number>(120); // 2 minutes in seconds
  const [showGracePeriod, setShowGracePeriod] = useState<boolean>(false);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [view, setView] = useState<'initial' | 'calendar' | 'price' | 'success' | 'decline' | 'custom_reply'>('initial');
  // House Held Logo Component
  const HouseHeldLogo = () => (
    <div className="flex items-center justify-center mb-4">
      <Shield className="h-8 w-8 text-househeld-700 mr-2" />
      <span className="text-2xl font-bold text-househeld-700">HouseHeld</span>
    </div>
  );
  
  // Common time slots for tradesperson visits
  const timeSlots = ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];
  
  // Load and validate the quote token
  useEffect(() => {
    const loadQuote = async () => {
      // Clear any existing errors
      setError(null);
      setErrorDetails(null);
      
      if (!token) {
        setError(ErrorType.TOKEN_INVALID);
        setErrorDetails('No token provided in the URL.');
        setIsLoading(false);
        return;
      }
      
      try {
        // Validate token
        const tokenData = await validateQuoteToken(token);
        if (!tokenData) {
          setError(ErrorType.TOKEN_EXPIRED);
          setErrorDetails('The access link has expired or is invalid.');
          setIsLoading(false);
          return;
        }
        
        try {
          // Load quote details
          const quoteData = await getQuoteById(tokenData.quoteId);
          if (!quoteData) {
            setError(ErrorType.QUOTE_NOT_FOUND);
            setErrorDetails('The requested quote could not be found. It may have been cancelled by the customer.');
            setIsLoading(false);
            return;
          }
          
          // Set quote data
          setQuote(quoteData);
          
          // If quote is already in a completed state, show appropriate view
          if (quoteData.phase === 'priced') {
            setView('success');
          } else if (quoteData.phase === 'visit_booked') {
            setView('success');
          } else if (quoteData.phase === 'declined') {
            setView('decline');
          }
        } catch (quoteError) {
          debug.error('Error loading quote:', quoteError);
          setError(ErrorType.SERVER_ERROR);
          setErrorDetails('There was a problem loading the quote details. Please try again later.');
        }
      } catch (tokenError) {
        debug.error('Error validating token:', tokenError);
        setError(ErrorType.TOKEN_INVALID);
        setErrorDetails('The access link is invalid or has been tampered with. Please request a new link.');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadQuote();
  }, [token]);
  
  // Book a visit
  // Handle grace period countdown
  useEffect(() => {
    let timer: number;
    
    if (showGracePeriod && graceTimeRemaining > 0) {
      timer = window.setInterval(() => {
        setGraceTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setShowGracePeriod(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [showGracePeriod, graceTimeRemaining]);
  
  // Book a visit
  const handleBookVisit = async () => {
    if (!quote || !selectedDate || !selectedTime) {
      toast.error('Please select both a date and time for your visit');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Create visit date from selected date and time
      const [hours, minutes] = selectedTime.split(':').map(Number);
      const visitDate = new Date(selectedDate);
      visitDate.setHours(hours, minutes, 0, 0);
      
      await bookQuoteVisit(quote.id, visitDate.toISOString());
      
      // Refresh quote data
      const updatedQuote = await getQuoteById(quote.id);
      setQuote(updatedQuote);
      
      toast.success('Visit scheduled successfully!');
      setView('success');
      setGraceTimeRemaining(120); // Reset to 2 minutes
      setShowGracePeriod(true);
    } catch (err) {
      toast.error('Failed to schedule visit. Please try again.');
      debug.error('Error booking visit:', err);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Send price quote
  const handleSendPrice = async () => {
    if (!quote || !priceText.trim()) {
      toast.error('Please enter a price quote');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      await submitQuotePrice(quote.id, priceText);
      
      // Refresh quote data
      const updatedQuote = await getQuoteById(quote.id);
      setQuote(updatedQuote);
      
      toast.success('Price quote sent successfully!');
      setView('success');
      setGraceTimeRemaining(120); // Reset to 2 minutes
      setShowGracePeriod(true);
    } catch (err) {
      toast.error('Failed to send price. Please try again.');
      debug.error('Error sending price:', err);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Decline the quote
  // Send custom reply
  const handleSendCustomReply = async () => {
    if (!quote || !customReply.trim()) {
      toast.error('Please enter your reply');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      await submitQuotePrice(quote.id, customReply);
      
      // Refresh quote data
      const updatedQuote = await getQuoteById(quote.id);
      setQuote(updatedQuote);
      
      toast.success('Your reply has been sent successfully!');
      setView('success');
      setGraceTimeRemaining(120); // Reset to 2 minutes
      setShowGracePeriod(true);
    } catch (err) {
      toast.error('Failed to send reply. Please try again.');
      debug.error('Error sending custom reply:', err);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDecline = async () => {
    if (!quote) return;
    
    // Confirm before declining
    if (!window.confirm('Are you sure you want to decline this job request? We\'ll let the customer know you\'re unavailable at this time.')) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      await declineQuote(quote.id, 'Tradesperson unavailable');
      
      // Refresh quote data
      const updatedQuote = await getQuoteById(quote.id);
      setQuote(updatedQuote);
      
      toast.success('Quote request declined');
      setView('decline');
    } catch (err) {
      toast.error('Failed to decline request. Please try again.');
      debug.error('Error declining quote:', err);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 bg-gray-50">
        <Card className="w-full max-w-md">
            <HouseHeldLogo />
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900"></div>
              <p className="text-center text-gray-500">Loading quote details...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Error state with specific error messages
  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center justify-center mb-2">
              {error === ErrorType.TOKEN_EXPIRED && (
                <Clock className="h-10 w-10 text-amber-500" />
              )}
              {error === ErrorType.TOKEN_INVALID && (
                <AlertCircle className="h-10 w-10 text-red-500" />
              )}
              {error === ErrorType.QUOTE_NOT_FOUND && (
                <Info className="h-10 w-10 text-blue-500" />
              )}
              {error === ErrorType.SERVER_ERROR && (
                <AlertCircle className="h-10 w-10 text-red-500" />
              )}
            </div>
            <CardTitle className="text-center">
              {error === ErrorType.TOKEN_EXPIRED && 'Link Expired'}
              {error === ErrorType.TOKEN_INVALID && 'Invalid Link'}
              {error === ErrorType.QUOTE_NOT_FOUND && 'Quote Not Found'}
              {error === ErrorType.SERVER_ERROR && 'Server Error'}
              {error === ErrorType.UNKNOWN && 'Something Went Wrong'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center mb-4">{errorDetails}</p>
            
            {error === ErrorType.TOKEN_EXPIRED && (
              <p className="text-center text-sm text-gray-500 mb-4">
                For security reasons, this link has expired. Please contact the customer 
                who sent you this request to get a new link.
              </p>
            )}
            
            {error === ErrorType.TOKEN_INVALID && (
              <p className="text-center text-sm text-gray-500 mb-4">
                The link you clicked appears to be invalid. This might happen if the link was
                incorrectly copied or if it was modified. Please check the link and try again,
                or contact the customer for a new link.
              </p>
            )}
            
            {error === ErrorType.QUOTE_NOT_FOUND && (
              <p className="text-center text-sm text-gray-500 mb-4">
                The quote request you're trying to access no longer exists. It may have been
                cancelled by the customer or already completed.
              </p>
            )}
          </CardContent>
          <CardFooter className="flex justify-center">
            <Button variant="outline" onClick={() => window.location.href = "/"}>
              Return Home
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }
  
  // Success state (if already complete)
  if (view === 'success' && quote) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <HouseHeldLogo />
            <CardTitle className="text-center text-emerald-600 flex items-center justify-center">
              <CheckCircle className="h-6 w-6 mr-2" />
              Thank You!
              
              {showGracePeriod && (
                <div className="w-full mt-4 pt-4 border-t border-gray-200">
                  <div className="text-center">
                    <h4 className="font-medium text-sm mb-2">Grace Period</h4>
                    <p className="text-xs text-gray-600 mb-3">
                      You can still change your response for the next {Math.floor(graceTimeRemaining / 60)}:{(graceTimeRemaining % 60).toString().padStart(2, '0')}
                    </p>
                    <Button 
                      onClick={() => setView('initial')}
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      Change My Response
                    </Button>
                  </div>
                </div>
              )}
            </CardTitle>
            <CardDescription className="text-center">
              Your response has been submitted successfully.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center space-y-4">
              {quote.phase === 'visit_booked' && (
                <div className="text-center bg-blue-50 p-4 rounded-md border border-blue-100 w-full">
                  <h3 className="font-medium mb-1">Visit Scheduled</h3>
                  <p className="text-gray-600">
                    {quote.visit_at ? formatDate(quote.visit_at, true) : 'Date to be confirmed'}
                  </p>
                </div>
              )}
              {quote.phase === 'priced' && (
                <div className="text-center bg-green-50 p-4 rounded-md border border-green-100 w-full">
                  <h3 className="font-medium mb-1">Reply Sent</h3>
                  <p className="text-gray-600 mt-2">
                    You sent:
                  </p>
                  <p className="bg-white p-2 rounded mt-2 text-sm">
                    {quote.price_text}
                  </p>
                </div>
              )}
              
              <p className="text-sm text-gray-500 mt-6">
                The customer will be notified of your response. They may contact you directly if they have any questions.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Declined state
  if (view === 'decline') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <HouseHeldLogo />
            <CardTitle className="text-center flex items-center justify-center">
              <XCircle className="h-6 w-6 mr-2" />
              Response Sent
            </CardTitle>
            <CardDescription className="text-center">
              We've let the customer know you're unavailable at this time.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="mb-4">Thank you for letting the customer know. We understand that tradespeople get busy, and we've communicated this politely to the customer.</p>
            <p className="text-sm text-gray-500">
              If your availability changes, feel free to contact the customer directly.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Initial view (quote details and options)
  // Custom Reply view
  if (view === 'custom_reply' && quote) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <HouseHeldLogo />
            <CardTitle>Send a Reply</CardTitle>
            <CardDescription>
              Your message will be sent to the customer
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Textarea
                placeholder="Enter your message to the customer here..."
                value={customReply}
                onChange={(e) => setCustomReply(e.target.value)}
                className="min-h-[150px]"
              />
              <div className="text-xs text-gray-500 mt-2">
                Include any important details such as availability, pricing information, or questions you have about the job.
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="outline" onClick={() => setView('initial')}>
              Back
            </Button>
            <Button 
              onClick={handleSendCustomReply}
              disabled={!customReply.trim() || isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Reply'}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }
  
  if (view === 'initial' && quote) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <HouseHeldLogo />
            <CardTitle>Quote Request</CardTitle>
            <CardDescription>
              {quote.job?.title || 'New job request'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-md bg-blue-50 p-4 border border-blue-100 mb-4">
              <p className="text-sm text-blue-700">
                This is the quickest way for you to respond to this customer, whether you can do the work or not.
              </p>
            </div>
            <div className="rounded-md bg-gray-50 p-4 border border-gray-100">
              <p className="text-sm whitespace-pre-wrap">
                {quote.job?.description || 'No description provided'}
              </p>
            </div>
            
            <div className="flex flex-col space-y-2">
              <h3 className="text-sm font-medium">How would you like to respond?</h3>
              
              {/* Primary option: Custom Reply */}
              <Button 
                variant="default"
                className="flex items-center justify-center space-x-2 w-full p-4"
                onClick={() => setView('custom_reply')}
              >
                <Send className="h-5 w-5" />
                <span className="text-base">Send Message</span>
              </Button>
              
              <div className="mt-3 text-xs text-gray-500 text-center">
                Or choose one of these options:
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Button 
                  variant="outline" 
                  className="flex items-center justify-center space-x-2"
                  onClick={() => setView('calendar')}
                >
                  <CalendarIcon className="h-4 w-4" />
                  <span>Schedule Visit</span>
                </Button>
                
                <Button 
                  variant="outline"
                  className="flex items-center justify-center space-x-2"
                  onClick={() => setView('price')}
                >
                  <Send className="h-4 w-4" />
                  <span>Send Price</span>
                </Button>
              </div>

              <Button 
                variant="outline"
                className="flex items-center justify-center space-x-2 mt-2"
                onClick={() => setView('custom_reply')}
              >
                <Send className="h-4 w-4" />
                <span>Write Custom Reply</span>
              </Button>
              
              <Button 
                variant="ghost" 
                className="text-red-600 hover:text-red-700 hover:bg-red-50 mt-4"
                onClick={handleDecline}
                disabled={isSubmitting}
              >
                <XCircle className="h-4 w-4 mr-2" />
                <span>Decline Politely</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Calendar view (select date for visit)
  if (view === 'calendar' && quote) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <HouseHeldLogo />
            <CardTitle>Schedule Visit</CardTitle>
            <CardDescription>
              Select a date and time for your visit
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              disabled={(date) => date < new Date() || date < new Date(new Date().setDate(new Date().getDate() - 1))}
              className="mx-auto"
            />
            
            {selectedDate && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Select Time</h3>
                <div className="grid grid-cols-3 gap-2">
                  {timeSlots.map(time => (
                    <Button
                      key={time}
                      variant={selectedTime === time ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedTime(time)}
                    >
                      {time}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="outline" onClick={() => setView('initial')}>
              Back
            </Button>
            <Button 
              onClick={handleBookVisit}
              disabled={!selectedDate || !selectedTime || isSubmitting}
            >
              {isSubmitting ? 'Scheduling...' : 'Confirm Visit'}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }
  
  // Price quote view
  if (view === 'price' && quote) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Send Price Quote</CardTitle>
            <CardDescription>
              Provide a price estimate for this job
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Textarea
                placeholder="Enter your price quote and any details about the work..."
                value={priceText}
                onChange={(e) => setPriceText(e.target.value)}
                className="min-h-[150px]"
              />
              <div className="text-xs text-gray-500 mt-2">
                Include any important details about the price, such as if it's an estimate or fixed price, 
                what's included, and any payment terms.
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="outline" onClick={() => setView('initial')}>
              Back
            </Button>
            <Button 
              onClick={handleSendPrice}
              disabled={!priceText.trim() || isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Quote'}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }
  
  // Fallback in case of unexpected state
  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-6 bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center">Something Went Wrong</CardTitle>
          <CardDescription className="text-center">
            We encountered an unexpected error. Please refresh the page and try again.
          </CardDescription>
        </CardHeader>
        <CardFooter className="flex justify-center">
          <Button onClick={() => window.location.reload()}>
            Refresh Page
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}